/**
 * Notification utilities for consistent message translation across all notification types
 */

import { getArabicStepName } from './workflowStepNames';

// ---------------------------------------------------------
// Type-Specific Message Builders
// ---------------------------------------------------------

function buildLeaveMessage(notification, senderName, isArabic) {
  let serverMsg = notification.rawServerMessage || '';
  const serverContainsArabic = /[\u0600-\u06FF]/.test(serverMsg);

  if (isArabic && !serverContainsArabic && (serverMsg.includes("Your leave request") || serverMsg.includes("leave request"))) {
    serverMsg = serverMsg.replace(/Your leave request/gi, "تم إرسال طلب الإجازة");
    serverMsg = serverMsg.replace(/leave request/gi, "طلب الإجازة");
    serverMsg = serverMsg.replace(/from\s+(\d{1,2}\/\d{1,2}\/\d{4})/gi, "من $1");
    serverMsg = serverMsg.replace(/to\s+(\d{1,2}\/\d{1,2}\/\d{4})/gi, "إلى $1");
    serverMsg = serverMsg.replace(/\s+has been submitted successfully and is pending approval\.?/gi, " بنجاح وهو بانتظار الموافقة.");
    serverMsg = serverMsg.replace(/\s+has been submitted successfully\.?/gi, " بنجاح.");
    serverMsg = serverMsg.replace(/\s+has been submitted\s+(?=بنجاح)/gi, " ");
    serverMsg = serverMsg.replace(/\s+successfully\.?/gi, " بنجاح.");
    serverMsg = serverMsg.replace(/Sick Leave/gi, "إجازة مرضية");
    serverMsg = serverMsg.replace(/Annual Leave/gi, "إجازة سنوية");
    serverMsg = serverMsg.replace(/Unpaid Leave/gi, "إجازة بدون أجر");
    serverMsg = serverMsg.replace(/Casual Leave/gi, "إجازة عارضة");
    serverMsg = serverMsg.replace(/Maternity Leave/gi, "إجازة وضع");
    serverMsg = serverMsg.replace(/Emergency Leave/gi, "إجازة طارئة");
    return serverMsg;
  } 
  
  if (!isArabic && serverContainsArabic && (serverMsg.includes("تم إرسال طلب الإجازة") || serverMsg.includes("إرسال طلب الإجازة"))) {
    serverMsg = serverMsg.replace(/تم إرسال طلب الإجازة/g, "Your leave request");
    serverMsg = serverMsg.replace(/إرسال طلب الإجازة/g, "Leave request");
    serverMsg = serverMsg.replace(/من\s+(\d{1,2}\/\d{1,2}\/\d{4})/g, "from $1");
    serverMsg = serverMsg.replace(/إلى\s+(\d{1,2}\/\d{1,2}\/\d{4})/g, "to $1");
    serverMsg = serverMsg.replace(/\s+بنجاح وهو بانتظار الموافقة\.?/g, " has been submitted successfully and is pending approval.");
    serverMsg = serverMsg.replace(/\s+بنجاح\.?/g, " has been submitted successfully.");
    serverMsg = serverMsg.replace("إجازة مرضية", "Sick Leave");
    serverMsg = serverMsg.replace("إجازة سنوية", "Annual Leave");
    serverMsg = serverMsg.replace("إجازة بدون أجر", "Unpaid Leave");
    serverMsg = serverMsg.replace("إجازة بدون راتب", "Unpaid Leave");
    serverMsg = serverMsg.replace("إجازة طارئة", "Emergency Leave");
    serverMsg = serverMsg.replace("إجازة عارضة", "Casual Leave");
    serverMsg = serverMsg.replace("إجازة وضع", "Maternity Leave");
    return serverMsg;
  }

  // Prefer the backend-generated message (which includes templates) if it matches the UI language
  if (serverMsg.trim() !== '') {
    if (isArabic && serverContainsArabic) return serverMsg;
    if (!isArabic && !serverContainsArabic) return serverMsg;
  }

  // Handle status changes or manual actions
  const statusSource = (notification.rawServerMessage || notification.eventTitle || '').toLowerCase();
  const isPendingApproval = /pending approval|بانتظار الموافقة/.test(statusSource);
  const isRejected = /reject|rejected|رفض|مرفوض/.test(statusSource);
  const isApproved = !isPendingApproval && /approve|approved|completed|complete|finished|قبول|موافق|مكتمل|اكتمل|انتهى/.test(statusSource);

  if (isRejected) {
    if (isArabic) return `تم رفض طلب الإجازة الخاص بك بواسطة ${senderName}`;
    return `Your leave request was rejected by ${senderName}`;
  }
  if (isApproved) {
    if (isArabic) return `تم الموافقة على طلب الإجازة الخاص بك بواسطة ${senderName}`;
    return `Your leave request was approved by ${senderName}`;
  }

  // Summary
  const leaveType = notification.leaveType || '';
  if (isArabic) return leaveType ? `${senderName} قدم طلب إجازة (${leaveType})` : `${senderName} قدم طلب إجازة جديدة`;
  return leaveType ? `${senderName} submitted a leave request (${leaveType})` : `${senderName} submitted a new leave request`;
}

// Known document type key → { en, ar } display label. Lets us translate a
// doc type name found in a stored notification (which may be in either
// language, or the raw key itself) into whichever language the viewer's UI
// is currently in, instead of showing it verbatim.
const DOCUMENT_TYPE_LABELS = {
  contract: { en: 'Contract', ar: 'عقد' },
  payslip: { en: 'Payslip', ar: 'كشف راتب' },
  certificate: { en: 'Certificate', ar: 'شهادة' },
  policy: { en: 'Policy', ar: 'سياسة' },
  hr_letter: { en: 'HR Letter', ar: 'خطاب الموارد البشرية' },
  bank_hr_letter: { en: 'Bank HR Letter', ar: 'خطاب الموارد البشرية للبنك' },
  experience_certificate: { en: 'Experience Certificate', ar: 'شهادة خبرة' },
};

/** Resolve any known spelling (raw key, English label, or Arabic label) of a
 * document type to the display label for the requested language. Falls back
 * to the original string unchanged if it isn't a recognized type. */
function translateDocType(docType, isArabic) {
  if (!docType) return docType;
  // Normalize away whitespace and the Arabic definite article "ال" so
  // slightly different phrasings of the same type (backend vs. frontend
  // dictionaries drifted independently) still match — e.g. "خطاب موارد
  // بشرية للبنك" (backend) vs "خطاب الموارد البشرية للبنك" (frontend).
  const normalize = (s) => String(s).trim().toLowerCase().replace(/\s+/g, ' ').replace(/ال/g, '');
  const needle = normalize(docType);
  for (const [key, labels] of Object.entries(DOCUMENT_TYPE_LABELS)) {
    if (needle === normalize(key) || needle === normalize(labels.en) || needle === normalize(labels.ar)) {
      return isArabic ? labels.ar : labels.en;
    }
  }
  return docType;
}

function buildDocumentMessage(notification, senderName, isArabic) {
  const serverText = String(notification.rawServerMessage || notification.eventTitle || '');
  const statusSource = serverText.toLowerCase();

  // Only trust the raw stored text as-is when it's ENTIRELY in the viewer's
  // language — matching the right sentence template is not enough, since an
  // otherwise-English sentence can embed an Arabic document type name (e.g.
  // "requested a خطاب موارد بشرية للبنك") and would pass a template-only
  // check. Require the opposite script to be entirely absent too.
  const matchesEnglishTemplate = /requested\s+a\s+|request\s+for\s+.+\s+has\s+been|uploaded\s+a\s+/i.test(serverText);
  const matchesArabicTemplate = /طلب\s|تم\s+(إرسال|إكمال|رفض|رفع)\s|رفع\s+مستند/.test(serverText);
  const containsArabicLetters = /[\u0600-\u06FF]/.test(serverText);
  const containsLatinLetters = /[A-Za-z]/.test(serverText);
  if (serverText.trim() !== '') {
    if (isArabic && matchesArabicTemplate && !containsLatinLetters) return serverText;
    if (!isArabic && matchesEnglishTemplate && !containsArabicLetters) return serverText;
  }

  const isRejected = /reject|rejected|رفض|مرفوض/.test(statusSource);
  const isAccepted = /accept|accepted|approve|approved|completed|complete|finished|قبول|موافق|مكتمل|اكتمل|انتهى/.test(statusSource);
  // "رفع" (uploaded) vs "رفض" (rejected) differ only in the last letter —
  // deliberately not folded into isRejected/isAccepted above.
  const isUploaded = /uploaded|رفع\s+مستند|رفع\s+المستند/.test(statusSource);

  const parsedDocTypeMatch = serverText.match(/requested\s+a\s+(.+?)(?:\s*\n|$)/i)
                          || serverText.match(/request\s+for\s+(.+?)\s+has\s+been/i)
                          || serverText.match(/المستند\s*\((.+?)\)/i)
                          || serverText.match(/طلب\s+(.+?)(?:\s*\n|$)/)
                          || serverText.match(/\((.+?)\)/)
                          || serverText.match(/uploaded\s+a\s+(.+?)\s+document/i)
                          || serverText.match(/رفع\s+مستند\s+(.+?)\s+من\s+قبل/);
  const rawDocType = notification.documentType || (parsedDocTypeMatch ? parsedDocTypeMatch[1].trim() : null) || (isArabic ? "مستند" : "document");
  const docType = translateDocType(rawDocType, isArabic);

  const senderIsNumeric = /^\d+$/.test(String(senderName || '').trim());
  const parsedRequesterMatch = serverText.match(/^(.+?)\s+requested\s+a\s+/i)
                             || serverText.match(/^(.+?)\s+طلب\s+/)
                             || serverText.match(/^(.+?)\s+uploaded\s+a\s+/i)
                             || serverText.match(/رفع\s+مستند\s+.+?\s+من\s+قبل\s+(.+)/);
  const nameFromMessage = parsedRequesterMatch ? parsedRequesterMatch[1].trim() : null;
  const displayName = senderIsNumeric ? (nameFromMessage || notification.fromName || senderName) : senderName;

  if (isRejected) {
    return isArabic ? `تم رفض طلب المستند الخاص بك${docType && docType !== 'مستند' ? ` (${docType})` : ''}` : `Your document request has been rejected.`;
  }
  if (isAccepted) {
    return isArabic ? `تم إكمال طلب المستند الخاص بك${docType && docType !== 'مستند' ? ` (${docType})` : ''}` : `Your document request has been accepted.`;
  }
  if (isUploaded) {
    // Two shapes: "{name} uploaded a {docType} document" (admin alert,
    // someone else's upload) vs. a self-confirmation with no name at all
    // ("Your {docType} document was uploaded successfully...").
    if (nameFromMessage) {
      return isArabic ? `${displayName} رفع مستند ${docType}` : `${displayName} uploaded a ${docType} document`;
    }
    return isArabic
      ? `تم رفع مستند (${docType}) بنجاح وهو الآن في انتظار موافقة المدير.`
      : `Your ${docType} document was uploaded successfully and is awaiting manager approval.`;
  }

  return isArabic ? `${displayName} طلب ${docType}` : `${displayName} requested a ${docType}`;
}

function buildEmployeeActionMessage(notification, senderName, isArabic) {
  const parsed = notification.parsedData;
  const serverMsg = notification.rawServerMessage;

  if (parsed && (parsed.en || parsed.ar || parsed.actionType)) {
    const actionType = parsed.actionType || '';
    const nameEn = parsed.en || parsed.enName || parsed.nameEn || '';
    const nameAr = parsed.ar || parsed.arName || parsed.nameAr || '';

    if (parsed.enTitle || parsed.arTitle) {
      return isArabic ? (parsed.arTitle || `${actionType} - ${nameAr}`) : (parsed.enTitle || `${actionType} - ${nameEn}`);
    }

    const actionLabelsEn = {
      salary_change: 'Salary Change', rehire: 'Rehire', promote: 'Promote', demote: 'Demote', transfer: 'Transfer',
      terminate: 'Terminate', suspend: 'Suspend', contract: 'Contract', rotation: 'Rotation', review: 'Review', training: 'Training',
    };
    const actionLabelsAr = {
      salary_change: 'تغيير راتب', rehire: 'إعادة توظيف', promote: 'ترقية', demote: 'تخفيض رتبة', transfer: 'نقل',
      terminate: 'إنهاء خدمة', suspend: 'إيقاف', contract: 'عقد', rotation: 'دوران', review: 'مراجعة', training: 'تدريب',
    };

    const labelEn = actionLabelsEn[actionType] || actionType;
    const labelAr = actionLabelsAr[actionType] || actionType;
    return isArabic ? `${labelAr} - ${nameAr || nameEn}` : `${labelEn} - ${nameEn || nameAr}`;
  }

  // Fallback heuristics
  const employeeName = (notification.eventTitle || '').toString();
  const isSalary = /تغيير راتب|راتب|salary|salary_change/i.test(serverMsg || notification.actionType || '');
  const nums = (serverMsg || '').match(/\d{3,}(?:[\.,]\d+)?/g) || [];

  if (isArabic) {
    if (isSalary) {
      if (employeeName && nums[0] && nums[1]) return `تم تقديم طلب تغيير راتب لـ ${employeeName} من ${nums[0]} إلى ${nums[1]}`;
      if (employeeName && nums[0]) return `تم تقديم طلب تغيير راتب لـ ${employeeName} إلى ${nums[0]}`;
      return employeeName ? `تم تقديم طلب تغيير راتب لـ ${employeeName}` : `تم تقديم طلب تغيير راتب`;
    }
    return employeeName ? `${employeeName} تم إنشاء طلب جديد` : `تم إنشاء طلب جديد`;
  }

  if (isSalary) {
    if (employeeName && nums[0] && nums[1]) return `Salary change request for ${employeeName}: ${nums[0]} → ${nums[1]}`;
    if (employeeName && nums[0]) return `Salary change request for ${employeeName}: ${nums[0]}`;
    return employeeName ? `Salary change request for ${employeeName}` : `Salary change request`;
  }
  return employeeName ? `New action request for ${employeeName}` : `New action request`;
}

function buildAdvancementMessage(notification, senderName, isArabic) {
  const { amount, installments } = notification;
  const serverText = String(notification.rawServerMessage || notification.eventTitle || '');
  const src = serverText.toLowerCase();
  
  // Prefer the backend-generated message (which includes templates) if it matches the UI language
  const serverContainsArabic = /[\u0600-\u06FF]/.test(serverText);
  if (serverText.trim() !== '') {
    if (isArabic && serverContainsArabic) return serverText;
    if (!isArabic && !serverContainsArabic) return serverText;
  }

  const advSenderIsNumeric = /^\d+$/.test(String(senderName || '').trim());
  const advDisplayName = advSenderIsNumeric ? (notification.fromName || senderName) : senderName;

  if (/reject|rejected|رفض|مرفوض|رفض الطلب/.test(src)) {
    return isArabic ? `تم رفض طلب السلفة الخاصة بك بواسطة ${advDisplayName}` : `${advDisplayName} rejected your advancement request`;
  }
  if (/approve|approved|مواف/.test(src)) {
    return isArabic ? `تم الموافقة على طلب السلفة الخاصة بك بواسطة ${advDisplayName}` : `${advDisplayName} approved your advancement request`;
  }
  if (/disburs|disbursed|صرف/.test(src)) {
    return isArabic ? `تم صرف السلفة الخاصة بك بواسطة ${advDisplayName}` : `${advDisplayName} disbursed the advancement`;
  }

  if (isArabic) {
    let base = `${advDisplayName} قدم طلب سلفة`;
    if (amount) base += ` (المبلغ: ${amount})`;
    if (installments) base += ` — الأقساط: ${installments}`;
    return base;
  }

  let base = `${advDisplayName} submitted an advancement request`;
  if (amount) base += ` (Amount: ${amount})`;
  if (installments) base += ` — Installments: ${installments}`;
  return base;
}

function buildCalendarMessage(notification, senderName, isArabic) {
  const eventTitle = notification.eventTitle || (isArabic ? "حدث جديد" : "New Event");
  const src = (notification.rawServerMessage || '').toLowerCase();

  if (/declin|رفض/.test(src)) return isArabic ? `${senderName} رفض دعوتك إلى الحدث: ${eventTitle}` : `${senderName} declined your invitation to "${eventTitle}"`;
  if (/accept|confirm|قبل|وافق/.test(src)) return isArabic ? `${senderName} وافق على دعوتك إلى الحدث: ${eventTitle}` : `${senderName} accepted your invitation to "${eventTitle}"`;
  if (/maybe|tentative|ربما/.test(src)) return isArabic ? `${senderName} رد بـ"ربما" على دعوتك إلى الحدث: ${eventTitle}` : `${senderName} replied "Maybe" to your invitation to "${eventTitle}"`;

  return isArabic ? `دعوة جديدة من ${senderName} إلى الحدث: ${eventTitle}` : `New invitation from ${senderName} for "${eventTitle}"`;
}

function buildEmployeeChangeMessage(notification, senderName, isArabic) {
  return isArabic ? `${senderName} طلب تغيير في بيانات الموظف` : `${senderName} requested an employee data change`;
}

function buildChatMessage(notification, senderName, isArabic) {
  // ONE global message notification per recipient, listing every distinct
  // sender who messaged (across all chats) since it was last read. Rendered
  // as a single headline — the sender list is the whole content, so there's
  // no separate body or call-to-action repeating it. "رسالة جديدة من X" keeps
  // the Arabic free of a gendered verb, since a sender's gender is unknown.
  const senders = Array.isArray(notification.senders) && notification.senders.length > 0
    ? notification.senders
    : null;
  const names = senders
    ? senders.map((s) => s.resolvedName || s.name || senderName)
    : [senderName];
  const [first, second] = names;
  const others = names.length - 2;

  if (isArabic) {
    if (names.length === 1) return `رسالة جديدة من ${first}`;
    if (names.length === 2) return `رسائل جديدة من ${first} و${second}`;
    return `رسائل جديدة من ${first} و${second} و${others === 1 ? 'شخص آخر' : `${others} آخرين`}`;
  }
  if (names.length === 1) return `New message from ${first}`;
  if (names.length === 2) return `New messages from ${first} and ${second}`;
  return `New messages from ${first}, ${second} and ${others === 1 ? '1 other' : `${others} others`}`;
}

function buildGenericMessage(notification, senderName, isArabic) {
  const serverMsg = notification.rawServerMessage || '';
  if (serverMsg && serverMsg.length > 0) {
    const redactEmail = (s) => s.replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '');
    const redactPhone = (s) => s.replace(/(\+?\d[\d\s().-]{6,}\d)/g, '');
    const numericPattern = /\b\d{4,}\b/g;
    
    let msg = redactPhone(redactEmail(serverMsg));
    // Check if we shouldn't replace digits (e.g. Arabic text with dates)
    const serverContainsArabic = /[\u0600-\u06FF]/.test(serverMsg);
    if (isArabic && serverContainsArabic) {
      msg = msg.replace(numericPattern, (match, offset, fullStr) => {
        const before = offset > 0 ? fullStr[offset - 1] : '';
        const after = offset + match.length < fullStr.length ? fullStr[offset + match.length] : '';
        if (before === '/' || after === '/') return match;
        if (before === '-' || after === '-') return match;
        return senderName;
      });
    } else {
      msg = msg.replace(numericPattern, senderName);
    }
    return msg;
  }
  return isArabic ? `إشعار جديد من ${senderName}` : `New notification from ${senderName}`;
}

function buildManualLeaveMessage(notification, senderName, isArabic) {
  let startDate = '';
  let endDate = '';
  let leaveType = notification.leaveType || '';
  
  // Extract dates from the English or Arabic message if present
  const serverMsg = notification.rawServerMessage || '';
  const enDateMatch = serverMsg.match(/from (\d{1,2}\/\d{1,2}\/\d{4}) to (\d{1,2}\/\d{1,2}\/\d{4})/i);
  const arDateMatch = serverMsg.match(/من (\d{1,2}\/\d{1,2}\/\d{4}) إلى (\d{1,2}\/\d{1,2}\/\d{4})/i);
  
  if (enDateMatch) {
    startDate = enDateMatch[1];
    endDate = enDateMatch[2];
  } else if (arDateMatch) {
    startDate = arDateMatch[1];
    endDate = arDateMatch[2];
  }
  
  // Extract Leave Type if not explicitly provided
  if (!leaveType) {
      const typeMatch = serverMsg.match(/(?:Leave Type|نوع الإجازة):\s*(.+?)\.?$/i);
      if (typeMatch) leaveType = typeMatch[1].trim();
  }

  // Translate leave type
  if (isArabic && leaveType) {
      const lowerType = leaveType.toLowerCase();
      if (lowerType.includes('annual') || lowerType.includes('سنوية')) leaveType = "إجازة سنوية";
      else if (lowerType.includes('sick') || lowerType.includes('مرضية')) leaveType = "إجازة مرضية";
      else if (lowerType.includes('maternity') || lowerType.includes('وضع')) leaveType = "إجازة وضع";
      else if (lowerType.includes('emergency') || lowerType.includes('طارئة')) leaveType = "إجازة طارئة";
      else if (lowerType.includes('unpaid') || lowerType.includes('بدون أجر') || lowerType.includes('بدون راتب')) leaveType = "إجازة بدون أجر";
      else if (lowerType.includes('casual') || lowerType.includes('عارضة')) leaveType = "إجازة عارضة";
      else if (lowerType.includes('wfh') || lowerType.includes('منزل')) leaveType = "عمل من المنزل";
  } else if (!isArabic && leaveType) {
      const lowerType = leaveType.toLowerCase();
      if (lowerType.includes('سنوية')) leaveType = "Annual Leave";
      else if (lowerType.includes('مرضية')) leaveType = "Sick Leave";
      else if (lowerType.includes('وضع')) leaveType = "Maternity Leave";
      else if (lowerType.includes('طارئة')) leaveType = "Emergency Leave";
      else if (lowerType.includes('بدون أجر') || lowerType.includes('بدون راتب')) leaveType = "Unpaid Leave";
      else if (lowerType.includes('عارضة')) leaveType = "Casual Leave";
      else if (lowerType.includes('عمل من المنزل')) leaveType = "Work From Home";
  }

  if (isArabic) {
    if (startDate && endDate) {
      return `قام المسؤول بتعيين إجازة يدوية لك من ${startDate} إلى ${endDate}.${leaveType ? ` نوع الإجازة: ${leaveType}.` : ''}`;
    }
    return `قام المسؤول بتعيين إجازة يدوية لك.${leaveType ? ` نوع الإجازة: ${leaveType}.` : ''}`;
  } else {
    if (startDate && endDate) {
      return `A manual leave has been assigned to you by admin from ${startDate} to ${endDate}.${leaveType ? ` Leave Type: ${leaveType}.` : ''}`;
    }
    return `A manual leave has been assigned to you by admin.${leaveType ? ` Leave Type: ${leaveType}.` : ''}`;
  }
}

function buildGroupAddedMessage(notification, senderName, isArabic) {
  const parsed = notification.parsedData || {};
  let groupName = parsed.groupName || 'الجروب';
  
  if (!parsed.groupName) {
    const serverMsg = notification.rawServerMessage || '';
    const groupMatch = serverMsg.match(/جروب "(.*?)"/i) || serverMsg.match(/group "(.*?)"/i);
    if (groupMatch) groupName = groupMatch[1];
  }

  if (isArabic) {
    return `تم إضافتك في جروب "${groupName}" بواسطة ${senderName}`;
  } else {
    return `You were added to group "${groupName}" by ${senderName}`;
  }
}

// ---------------------------------------------------------
// Main Dispatcher
// ---------------------------------------------------------

const handlers = {
  LEAVE_REQUEST: buildLeaveMessage,
  LEAVE_STATUS_UPDATE: buildLeaveMessage,
  LEAVE_CONFIRMATION: buildLeaveMessage,
  MANUAL_LEAVE_ASSIGNED: buildManualLeaveMessage,
  DOCUMENT_REQUEST: buildDocumentMessage,
  EMPLOYEE_ACTION: buildEmployeeActionMessage,
  ADVANCEMENT_REQUEST: buildAdvancementMessage,
  CALENDAR_EVENT: buildCalendarMessage,
  EMPLOYEE_CHANGE: buildEmployeeChangeMessage,
  MESSAGE: buildChatMessage,
  GROUP_ADDED: buildGroupAddedMessage,
};

// ---------------------------------------------------------
// Arabic cleanup for stored notification text
// ---------------------------------------------------------

const ARABIC_LETTERS = /[؀-ۿ]/;

/** "N days" with Arabic number agreement (mirrors the backend helper). */
function arabicDayCount(value) {
  const n = Number(value);
  if (!Number.isInteger(n) || n < 0) return `${value} يوم`;
  if (n === 1) return 'يوم واحد';
  if (n === 2) return 'يومان';
  const tail = n % 100;
  if (tail >= 3 && tail <= 10) return `${n} أيام`;
  if (tail >= 11) return `${n} يومًا`;
  return `${n} يوم`;
}

/**
 * Tidy Arabic notification text the backend stored before its templates were
 * fully localized, so already-delivered notifications read correctly too:
 * an English workflow step name quoted inside the Arabic sentence (a known
 * default is translated; a custom English name is left out), "اوفر تايم",
 * and "N يوم" without Arabic number agreement. Anything else is untouched.
 * @param {string} text
 * @returns {string}
 */
export function localizeStoredArabicText(text) {
  if (!text || typeof text !== 'string') return text;
  return text
    .replace(/لديك موافقة معلقة:\s*"([^"]*)"\s*على/g, (match, step) => {
      const known = getArabicStepName(step);
      if (known) return `لديك موافقة معلقة: "${known}" على`;
      return ARABIC_LETTERS.test(step) ? match : 'لديك موافقة معلقة على';
    })
    .replace(/اوفر تايم/g, 'عمل إضافي')
    .replace(/(^|[\s—])(\d+(?:\.\d+)?) يوم(?![؀-ۿ])/g, (match, lead, num) => `${lead}${arabicDayCount(num)}`);
}

/**
 * Build a localized notification message based on type and language
 * @param {Object} notification - The notification object (extracted via extractNotificationData)
 * @param {string} senderName - The resolved sender name
 * @param {string} language - Current language ('ar' or 'en')
 * @returns {string} Localized message
 */
export function buildNotificationMessage(notification, senderName, language) {
  const isArabic = language === 'ar';
  
  // Prefer exact type match
  let handler = handlers[notification.type];
  
  // Fallback to heuristic prefix/id matching
  if (!handler) {
    if (notification.type?.startsWith('LEAVE') || notification.leaveId) handler = buildLeaveMessage;
    else if (notification.type?.startsWith('DOCUMENT') || notification.documentId) handler = buildDocumentMessage;
    else if (notification.type?.startsWith('ADVANCEMENT') || notification.advancementId) handler = buildAdvancementMessage;
    else if (notification.type === 'EMPLOYEE_ACTION') handler = buildEmployeeActionMessage;
    else if (notification.type === 'EMPLOYEE_CHANGE' || notification.changeId) handler = buildEmployeeChangeMessage;
    else if (notification.type === 'MESSAGE' || notification.messageId) handler = buildChatMessage;
    else if (notification.type === 'CALENDAR_EVENT' || notification.eventId) handler = buildCalendarMessage;
    else handler = buildGenericMessage;
  }

  // For non-Arabic UI, if server message is clearly non-Arabic plain text, we can use it directly sometimes.
  const serverMsg = notification.rawServerMessage || '';
  const serverContainsArabic = /[\u0600-\u06FF]/.test(serverMsg);
  if (!notification.type && serverMsg && !isArabic && !notification.isJson && !serverContainsArabic) {
     handler = buildGenericMessage;
  }

  return handler(notification, senderName, isArabic);
}

/**
 * Resolve sender names from employee API
 * @param {Array} senderIds - Array of numeric sender IDs to resolve
 * @param {Object} cache - Ref object with .current holding the cache
 * @param {string} baseUrl - Base URL for API
 * @param {Function} authFetch - Optional authenticated fetch function
 * @returns {Promise<void>}
 */
export async function resolveSenderNames(senderIds, cache, baseUrl, authFetch = fetch) {
  if (!senderIds || senderIds.length === 0) return;

  await Promise.all(
    senderIds.map(async (id) => {
      try {
        const response = await authFetch(`${baseUrl}/employees/select/${id}`);
        if (!response.ok) return;

        const data = await response.json();
        const emp = data.employee || data.data || data;

        const enName = emp?.fullName || emp?.englishName || emp?.employeeName || emp?.name || emp?.sr || "";
        const arName = emp?.arabicName || emp?.arabic_name || emp?.arabicFullName || emp?.nameArabic || "";

        cache.current[id] = {
          en: enName || arName || id,
          ar: arName || enName || id,
        };
      } catch (e) {
        console.error(`Failed to resolve sender ${id}:`, e);
      }
    })
  );
}

/**
 * Extract notification metadata for message building
 * @param {Object} notification - Raw notification from API
 * @returns {Object} Structured notification data
 */
export function extractNotificationData(notification) {
  let parsed = null;
  const rawMsg = (notification.originalMessage || notification.message || '').toString().trim();
  
  if (rawMsg.startsWith('{')) {
    try {
      parsed = JSON.parse(rawMsg);
    } catch (e) {
      parsed = null;
    }
  }

  const type = notification.type || (notification.eventId ? 'CALENDAR_EVENT'
    : notification.leaveId ? 'LEAVE_REQUEST'
      : notification.documentId ? 'DOCUMENT_REQUEST'
        : notification.advancementId ? 'ADVANCEMENT_REQUEST'
          : notification.changeId ? 'EMPLOYEE_CHANGE'
            : notification.messageId ? 'MESSAGE'
              : (notification.title === 'Document Request' || /requested\s+a\s+/i.test(notification.message || '')) ? 'DOCUMENT_REQUEST'
                : 'info');

  return {
    ...notification,
    id: notification.id,
    from: notification.from,
    type,
    eventId: notification.eventId,
    leaveId: notification.leaveId,
    documentId: notification.documentId,
    advancementId: notification.advancementId,
    changeId: notification.changeId,
    messageId: notification.messageId,
    eventTitle: notification.title || notification.eventTitle,
    leaveType: notification.leaveType || parsed?.leaveType,
    documentType: notification.documentType || parsed?.documentType,
    originalMessage: notification.message,
    read: notification.read,
    createdAt: notification.createdAt,
    
    // Extracted structured fields
    parsedData: parsed,
    rawServerMessage: rawMsg,
    isJson: !!parsed,
    amount: notification.amount || parsed?.amount || notification.advancementAmount || notification.requestedAmount || notification.amountRequested,
    installments: notification.installments || parsed?.installments || notification.installmentCount || notification.repaymentTerms,
    installmentAmount: notification.installmentAmount || parsed?.installmentAmount || notification.repaymentAmount,
    reason: notification.reason || parsed?.reason || notification.request || notification.notes,
    actionType: notification.actionType || parsed?.actionType,
    fromName: notification.fromName || parsed?.fromName,
    repaymentStart: notification.repaymentStart || parsed?.repaymentStart || notification.repaymentStartDate || notification.repaymentStartAt,
    netSalary: notification.netSalary || parsed?.netSalary || notification.salaryNet,
    urgency: notification.urgency || parsed?.urgency || notification.priority || 'normal',
  };
}

/*
 * Build a one-line advancement summary (English)
 */
export function formatAdvancementOneLine(notification, senderName = '') {
  const amount = notification.amount || '';
  const installments = notification.installments || (notification.repayment && notification.repayment.count) || '';
  const installmentAmount = notification.installmentAmount || '';
  const repaymentStart = notification.repaymentStart || '';
  const netSalary = notification.netSalary || '';
  const reason = notification.reason || '';
  const urgency = notification.urgency || 'normal';

  let perInstallment = installmentAmount;
  if (!perInstallment && amount && installments) {
    const a = parseFloat(amount);
    const n = parseInt(installments, 10);
    if (!Number.isNaN(a) && !Number.isNaN(n) && n > 0) {
      perInstallment = (a / n).toFixed(2);
    }
  }

  let base = `${senderName} requests an advance`;
  if (amount) base += ` of ${amount}`;
  if (installments) {
    base += ` to be repaid in ${installments} monthly installments`;
    if (perInstallment) base += ` of ${perInstallment}`;
  }
  if (repaymentStart) base += ` (start: ${repaymentStart})`;
  if (netSalary) base += `; net salary ${netSalary}`;
  if (reason) base += `; reason: "${reason}"`;
  if (urgency !== 'normal') base += `; urgency: ${urgency}`;
  
  return base + '.';
}
